import { useEffect, useState } from 'react';
import type { CompanyProfile } from '@swg/contracts';
import { CONFIDENCE_LABELS, type Confidence } from '@swg/domain';
import { ApiError, confirmProfile, fetchProfile, patchProfile } from '../api.js';
import { NicheProcessForm, NicheProcessList } from './Niche.js';
import { theme } from '../theme.js';

const CONFIDENCE_COLOR: Record<Confidence, string> = {
  low: '#C99A2E',
  medium: theme.color.primary,
  high: '#1E7A44',
};

type FieldType = 'text' | 'number' | 'list';

interface FieldDef {
  key:
    | 'name'
    | 'industry'
    | 'services'
    | 'geographicFocus'
    | 'targetAudience'
    | 'propositions'
    | 'keywords'
    | 'legalForm'
    | 'employeeCount'
    | 'foundedYear'
    | 'tradeNames'
    | 'nicheSummary';
  label: string;
  type: FieldType;
  span: 1 | 2;
  /**
   * Tekst in plaats van "—" wanneer het veld leeg is (P26).
   *
   * Voor de niche is een streepje misleidend: het leest als "dit bedrijf heeft
   * geen focus" terwijl het betekent dat we er geen hebben kunnen vaststellen.
   * En het verzwijgt dat de analist hem zelf kan invullen — precies de vraag die
   * de gebruikersfeedback hier stelde.
   */
  emptyHint?: string;
}

// Volgorde en kolombreedte conform het goedgekeurde prototype. Rechtsvorm,
// werkzame personen, oprichtingsjaar en handelsnamen komen uit KVK (US02).
const FIELD_LAYOUT: FieldDef[] = [
  { key: 'name', label: 'Bedrijfsnaam', type: 'text', span: 1 },
  { key: 'industry', label: 'Branche', type: 'text', span: 1 },
  // P22 — direct onder de branche, want dit is precies het verschil dat de
  // gebruikersfeedback aanwees: de SBI omvat een hele familie behandelingen,
  // de niche zegt welke daarvan dit bedrijf daadwerkelijk levert.
  {
    key: 'nicheSummary',
    label: 'Focus / niche',
    type: 'text',
    span: 2,
    emptyHint: 'Nog niet vastgesteld — vul zelf in wat dit bedrijf werkelijk doet.',
  },
  { key: 'legalForm', label: 'Rechtsvorm', type: 'text', span: 1 },
  { key: 'employeeCount', label: 'Werkzame personen', type: 'number', span: 1 },
  { key: 'foundedYear', label: 'Opgericht in', type: 'number', span: 1 },
  { key: 'services', label: 'Belangrijkste producten / diensten', type: 'list', span: 2 },
  { key: 'geographicFocus', label: 'Geografische focus', type: 'text', span: 1 },
  { key: 'targetAudience', label: 'Doelgroep', type: 'text', span: 1 },
  { key: 'tradeNames', label: 'Handelsnamen', type: 'list', span: 2 },
  { key: 'propositions', label: 'Belangrijkste proposities', type: 'list', span: 2 },
  { key: 'keywords', label: 'Relevante zoekwoorden', type: 'list', span: 2 },
];

function toLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function textValue(p: CompanyProfile, key: FieldDef['key']): string {
  const v = p[key as 'name'];
  if (typeof v === 'number') return String(v);
  return (v as string | null) ?? '';
}

function listValue(p: CompanyProfile, key: FieldDef['key']): string[] {
  return (p[key as 'services'] as string[]) ?? [];
}

export function ProfileReview({
  runId,
  runActive,
  canEdit,
  onConfirmed,
  onBack,
}: {
  runId: string;
  runActive: boolean;
  canEdit: boolean;
  onConfirmed: () => void;
  onBack?: () => void;
}) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [state, setState] = useState<'loading' | 'none' | 'ready'>('loading');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // KVK is een apart, altijd-bewerkbaar veld (hoort bij het bedrijf, niet de versie).
  const [kvk, setKvk] = useState('');
  const [kvkBusy, setKvkBusy] = useState(false);
  const [kvkSaved, setKvkSaved] = useState(false);
  const [kvkError, setKvkError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchProfile(runId)
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setKvk(p.kvkNumber ?? '');
        setState('ready');
      })
      .catch(() => !cancelled && setState('none'));
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (state === 'loading') return null;
  if (state === 'none') {
    return runActive ? (
      <p style={{ color: theme.color.muted, fontSize: 13.5 }}>Het bedrijfsprofiel wordt opgesteld…</p>
    ) : null;
  }
  if (!profile) return null;

  const confirmed = profile.status === 'CONFIRMED';
  const editable = canEdit && !confirmed;

  function startEdit(f: FieldDef) {
    setError(null);
    setEditingField(f.key);
    setDraft(f.type === 'list' ? listValue(profile!, f.key).join('\n') : textValue(profile!, f.key));
  }

  async function saveField(f: FieldDef) {
    setError(null);
    setBusy(true);
    try {
      const trimmed = draft.trim();
      const patch =
        f.type === 'list'
          ? { [f.key]: toLines(draft) }
          : f.type === 'number'
            ? { [f.key]: trimmed === '' ? null : Number(trimmed) }
            : { [f.key]: trimmed };
      const updated = await patchProfile(runId, patch);
      setProfile(updated);
      setEditingField(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Een procesregel verwijderen is een handmatige correctie, geen bewerking van
   * de tekst: de analist zegt "dit leveren wij niet". De hele lijst gaat mee in
   * de patch, zodat de server de herkomst van het veld op MANUAL zet en een
   * volgende automatische stap hem niet stil overschrijft.
   */
  /** Een handmatig ingevoerde procesregel toevoegen (P26). */
  async function addProcess(entry: CompanyProfile['nicheProcesses'][number]) {
    if (!profile) return;
    setError(null);
    setBusy(true);
    try {
      setProfile(await patchProfile(runId, { nicheProcesses: [...profile.nicheProcesses, entry] }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Toevoegen mislukt.');
    } finally {
      setBusy(false);
    }
  }

  async function removeProcess(index: number) {
    if (!profile) return;
    setError(null);
    setBusy(true);
    try {
      const next = profile.nicheProcesses.filter((_, i) => i !== index);
      setProfile(await patchProfile(runId, { nicheProcesses: next }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm() {
    setError(null);
    setBusy(true);
    try {
      const updated = await confirmProfile(runId);
      setProfile(updated);
      onConfirmed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bevestigen mislukt.');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveKvk() {
    setKvkError(null);
    setKvkSaved(false);
    const value = kvk.trim();
    if (value !== '' && !/^\d{8}$/.test(value)) {
      setKvkError('KVK-nummer moet 8 cijfers zijn.');
      return;
    }
    setKvkBusy(true);
    try {
      const updated = await patchProfile(runId, { kvkNumber: value });
      setProfile(updated);
      setKvk(updated.kvkNumber ?? '');
      setKvkSaved(true);
    } catch (err) {
      setKvkError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setKvkBusy(false);
    }
  }

  // Unieke bronnen voor de "Gebruikte bronnen"-voettekst.
  const usedSources = Array.from(
    new Set(
      Object.values(profile.fieldMeta)
        .map((m) => m?.source)
        .filter((s): s is string => Boolean(s)),
    ),
  );

  const cardStyle = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: 10,
    padding: '16px 18px',
  } as const;

  const inputStyle = {
    width: '100%',
    padding: '9px 11px',
    fontSize: 14,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 8,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  };

  const badge = (bg: string, color: string, text: string) => (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        background: bg,
        color,
        borderRadius: 10,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
          Bedrijfsprofiel{profile.name ? ` — ${profile.name}` : ''}
        </h2>
        <div style={{ fontSize: 13, color: theme.color.muted }}>
          {confirmed ? (
            <span style={{ color: theme.color.ok, fontWeight: 600 }}>Bevestigd</span>
          ) : (
            'Automatisch opgesteld — controleer voordat de analyse verdergaat.'
          )}
        </div>
      </div>

      {!confirmed && (
        <div
          style={{
            background: theme.color.warnBg,
            border: `1px solid ${theme.color.warnBorder}`,
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13.5,
            color: '#6E5410',
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          <strong>Controleer dit profiel voordat de analyse verdergaat.</strong> Onzekere of
          afgeleide gegevens zijn gemarkeerd als <em>aanname</em>. Correcties worden bewaard en niet
          zonder waarschuwing overschreven.
        </div>
      )}

      {/* KVK-nummer (apart, altijd bewerkbaar) */}
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 7 }}>
          KVK-nummer <span style={{ textTransform: 'none', fontWeight: 400, color: theme.color.subtle }}>(automatisch gezocht op bedrijfsnaam en website; wijzig hier als dit niet klopt)</span>
        </div>
        {canEdit ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              style={{ ...inputStyle, maxWidth: 180 }}
              inputMode="numeric"
              placeholder="12345678"
              value={kvk}
              onChange={(e) => setKvk(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
            <button
              onClick={() => void onSaveKvk()}
              disabled={kvkBusy}
              style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: kvkBusy ? 'default' : 'pointer', fontFamily: 'inherit' }}
            >
              {kvkBusy ? 'Bezig…' : 'Bewaren'}
            </button>
            {kvkSaved && <span style={{ fontSize: 12.5, color: theme.color.ok }}>Opgeslagen ✓</span>}
          </div>
        ) : (
          <div style={{ fontSize: 14 }}>{kvk || <span style={{ color: theme.color.subtle }}>—</span>}</div>
        )}
        {kvkError && <div style={{ marginTop: 6, fontSize: 12.5, color: '#A82A2A' }}>{kvkError}</div>}
      </div>

      {/* Velden in 2-koloms grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {FIELD_LAYOUT.map((f) => {
          const meta = profile.fieldMeta[f.key];
          const isAssumption = meta?.origin === 'AI_SUGGESTED';
          const isKvk = meta?.origin === 'KVK';
          const isCorrected = meta?.origin === 'MANUAL';
          const editing = editingField === f.key;
          const list = f.type === 'list' ? listValue(profile, f.key) : [];
          const text = f.type === 'text' ? textValue(profile, f.key) : '';

          return (
            <div key={f.key} style={{ ...cardStyle, gridColumn: `span ${f.span}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {f.label}
                  </span>
                  {isAssumption && badge(theme.color.warnBg, '#8A6A12', 'AANNAME')}
                  {isKvk && badge('#E4F3EB', theme.color.ok, 'FEIT')}
                  {isCorrected && badge('#E4F3EB', theme.color.ok, 'GECORRIGEERD')}
                </div>
                {editable && !editing && (
                  <button
                    onClick={() => startEdit(f)}
                    style={{ background: 'none', border: 'none', color: theme.color.primary, fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', padding: 0, whiteSpace: 'nowrap' }}
                  >
                    Bewerken
                  </button>
                )}
              </div>

              {editing ? (
                <div>
                  {f.type === 'list' ? (
                    <textarea
                      style={{ ...inputStyle, minHeight: 76, resize: 'vertical' }}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Eén per regel"
                    />
                  ) : (
                    <input
                      style={inputStyle}
                      type={f.type === 'number' ? 'number' : 'text'}
                      inputMode={f.type === 'number' ? 'numeric' : undefined}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      onClick={() => void saveField(f)}
                      disabled={busy}
                      style={{ background: theme.color.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}
                    >
                      {busy ? 'Bezig…' : 'Opslaan'}
                    </button>
                    <button
                      onClick={() => setEditingField(null)}
                      disabled={busy}
                      style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : f.type === 'list' ? (
                list.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {list.map((v, i) => (
                      <span key={i} style={{ fontSize: 12.5, background: theme.color.accentBg, color: theme.color.primary, borderRadius: 6, padding: '3px 9px' }}>
                        {v}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: theme.color.subtle, fontSize: 14 }}>—</span>
                )
              ) : (
                <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                  {text || (
                    <span style={{ color: theme.color.subtle }}>{f.emptyHint ?? '—'}</span>
                  )}
                </div>
              )}

              {meta && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: theme.color.muted }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: CONFIDENCE_COLOR[meta.confidence], display: 'inline-block' }} />
                  <span>{CONFIDENCE_LABELS[meta.confidence]}</span>
                  <span>·</span>
                  <span>bron: {meta.source}</span>
                  {isAssumption && <span style={{ color: '#7c3aed', fontWeight: 600 }}>· AI-suggestie</span>}
                  {isKvk && <span style={{ color: theme.color.ok, fontWeight: 600 }}>· KVK Handelsregister</span>}
                  {isCorrected && <span style={{ color: theme.color.primary, fontWeight: 600 }}>· handmatig</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Procespakket (P20/P21) — bron per regel */}
      <div style={{ ...cardStyle, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Procespakket
          </span>
          {profile.fieldMeta.nicheProcesses?.origin === 'MANUAL'
            ? badge('#E4F3EB', theme.color.ok, 'GECORRIGEERD')
            : profile.nicheProcesses.length > 0 && badge('#E4F3EB', theme.color.ok, 'MET BRON')}
          <span style={{ fontSize: 12, color: theme.color.subtle }}>
            wat dit bedrijf aantoonbaar levert — dit bepaalt tegen wie we vergelijken
          </span>
        </div>
        <NicheProcessList
          processes={profile.nicheProcesses}
          onRemove={editable ? (i) => void removeProcess(i) : undefined}
          busy={busy}
        />
        {editable && <NicheProcessForm onAdd={(entry) => void addProcess(entry)} busy={busy} />}
      </div>

      {/* P27b — wat de AI vermoedt maar nergens kon aanwijzen. Apart gezet, met
          een eigen rand en een eigen kop: als dit tussen de bewezen regels zou
          staan, kon de lezer het verschil niet meer zien, en dat verschil is
          waar de hele bronherleidbaarheid op rust. */}
      {profile.unsourcedProcesses.length > 0 && (
        <div
          style={{
            ...cardStyle,
            marginTop: 14,
            borderStyle: 'dashed',
            background: theme.color.bg,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Vermoed, niet teruggevonden
            </span>
            {badge(theme.color.warnBg, '#8A6A12', 'GEEN BRON')}
          </div>
          <div style={{ fontSize: 12.5, color: theme.color.muted, marginBottom: 9, lineHeight: 1.5 }}>
            Dit noemt de AI wel, maar het stond op geen van de gelezen pagina&rsquo;s. Het telt niet
            mee bij het zoeken naar concurrenten en niet in de nichegrootte. Klopt een regel, voeg
            hem dan hierboven toe met de bron erbij.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {profile.unsourcedProcesses.map((p, i) => (
              <div key={`${p.process}-${i}`} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{p.process}</span>
                {p.rationale && (
                  <span style={{ color: theme.color.subtle }}> — {p.rationale}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gelezen pagina's (P27a) — maakt een leeg profielveld herleidbaar. */}
      {(profile.scannedPages.length > 0 || profile.scanNote) && (
        <div style={{ ...cardStyle, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Gelezen pagina&rsquo;s
            </span>
            <span style={{ fontSize: 12, color: theme.color.subtle }}>
              {profile.scannedPages.length} van de site gekozen — hierop is dit profiel gebaseerd
            </span>
          </div>
          {profile.scanNote && (
            <div
              style={{
                background: theme.color.warnBg,
                border: `1px solid ${theme.color.warnBorder}`,
                borderRadius: 8,
                padding: '9px 12px',
                fontSize: 12.5,
                color: '#6E5410',
                lineHeight: 1.5,
                marginBottom: 10,
              }}
            >
              {profile.scanNote}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {profile.scannedPages.map((pagina) => (
              <div key={pagina.url} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
                <a href={pagina.url} target="_blank" rel="noreferrer" style={{ color: theme.color.primary }}>
                  {(() => {
                    try {
                      return new URL(pagina.url).pathname || '/';
                    } catch {
                      return pagina.url;
                    }
                  })()}
                </a>
                <span style={{ color: theme.color.subtle }}>{pagina.reasonNl}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gebruikte bronnen */}
      {usedSources.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Gebruikte bronnen
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {usedSources.map((s) => (
              <span key={s} style={{ fontSize: 12.5, background: theme.color.bg, color: theme.color.text, border: `1px solid ${theme.color.border}`, borderRadius: 8, padding: '4px 10px' }}>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: '#FBE6E6', border: '1px solid #EFC2C2', color: '#A82A2A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 14 }}>
          {error}
        </div>
      )}

      {/* Onderste acties */}
      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
          <button
            onClick={() => onBack?.()}
            disabled={!onBack}
            style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: onBack ? 'pointer' : 'default', opacity: onBack ? 1 : 0.5, fontFamily: 'inherit' }}
          >
            ← Terug
          </button>
          <button
            onClick={() => void onConfirm()}
            disabled={busy}
            style={{ background: theme.color.cta, color: theme.color.ctaText, border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}
          >
            {busy ? 'Bezig…' : 'Profiel bevestigen → branche'}
          </button>
        </div>
      )}
    </div>
  );
}
